import React, { useEffect, useState } from "react";
import { gql, useApolloClient, useMutation } from "@apollo/client";
import { pdf } from "@react-pdf/renderer";

import { RotaButtonNew, RotaCheckbox, iconNew } from "@teamrota/rota-design";

import { errorModal } from "~/src/utils/errors";

import createOrUpdateVenue from "../graphql/create-update-venue-templates";
import getProfile from "~/src/graphql/queries/get-profile/get-profile-query.decorator";
import styles, { TableDiv, Th, SaveButtonWrapper } from "./check-in-styles";
import { QrCodePdf } from "./qr-code-pdf";

const { Download } = iconNew;

const PROVIDER_PAGE_SIZE = 100;

const GENERATE_QR_CODE = gql`
  mutation GenerateQrCode($venueId: ID!) {
    generateQrCode(venueId: $venueId) {
      qrData
    }
  }
`;

const GET_QR_CODE_PROVIDERS = gql`
  query GetQrCodeProviders($limit: Int!, $offset: Int!) {
    account {
      connections(
        accountType: requester
        limit: $limit
        offset: $offset
        orderByProp: accountName
        orderByDirection: ASC
      ) {
        data {
          id
          sourceAccount {
            id
            accountName
            logo
          }
        }
        totalResults
      }
    }
  }
`;

const CheckInLocationForm = ({ user, createOrUpdateVenue, setIsModalOpen }) => {
  const { venues } = user?.account;
  const apolloClient = useApolloClient();
  const [generateQrCode] = useMutation(GENERATE_QR_CODE);

  const [isStateLoading, setIsStateLoading] = useState(false);
  const [downloadingVenueId, setDownloadingVenueId] = useState();
  const [shouldShowSavedState, setShouldShowSavedState] = useState(false);
  const [updatedVenues, setUpdatedVenues] = useState();

  useEffect(() => {
    setUpdatedVenues(venues);
  }, []);

  const saveVenue = async event => {
    event.preventDefault();
    setIsStateLoading(true);

    try {
      updatedVenues.forEach(async venue => {
        await createOrUpdateVenue(venue);
      });

      setIsStateLoading(false);
      setShouldShowSavedState(true);
      setTimeout(() => {
        setIsModalOpen(false);
      }, 1500);
    } catch (e) {
      setIsStateLoading(false);
      errorModal(e);
    }
  };

  const handlePinCodeChecked = item => {
    setUpdatedVenues(
      updatedVenues?.map(venue => {
        if (venue?.id === item.id) {
          const newVenue = { ...venue };
          newVenue.isPinCodeCheckInEnabled = !item.isPinCodeCheckInEnabled;
          return newVenue;
        }
        return venue;
      })
    );
    setShouldShowSavedState(false);
  };

  const handleQrCodeChecked = item => {
    setUpdatedVenues(
      updatedVenues?.map(venue => {
        if (venue?.id === item?.id) {
          return {
            ...venue,
            isQrCodeCheckInEnabled: !item.isQrCodeCheckInEnabled
          };
        }
        return venue;
      })
    );
    setShouldShowSavedState(false);
  };

  const clearAllCheckBoxHandler = async event => {
    event.preventDefault();

    setUpdatedVenues(
      updatedVenues?.map(venue => {
        const newVenue = { ...venue };
        newVenue.isQrCodeCheckInEnabled = false;
        newVenue.isPinCodeCheckInEnabled = false;
        return newVenue;
      })
    );
  };

  const getQrCodeProviders = async () => {
    const providers = [];
    let offset = 0;
    let totalResults;

    do {
      const { data } = await apolloClient.query({
        query: GET_QR_CODE_PROVIDERS,
        variables: {
          limit: PROVIDER_PAGE_SIZE,
          offset
        },
        fetchPolicy: "network-only"
      });
      const connections = data?.account?.connections;
      const page = connections?.data || [];

      providers.push(
        ...page.map(connection => connection?.sourceAccount).filter(Boolean)
      );
      offset += page.length;
      totalResults = connections?.totalResults || 0;

      if (!page.length) break;
    } while (offset < totalResults);

    return [
      ...new Map(providers.map(provider => [provider.id, provider])).values()
    ];
  };

  const qrCodeDownload = async venue => {
    setDownloadingVenueId(venue.id);

    try {
      const connectedProviders = await getQrCodeProviders();
      const providers = connectedProviders.length
        ? connectedProviders
        : [
            {
              id: user.account.id,
              accountName: user.account.accountName,
              logo: user.account.logo
            }
          ];
      const { data } = await generateQrCode({
        variables: { venueId: venue.id }
      });
      const qrData = data?.generateQrCode?.qrData;

      if (!qrData) throw new Error("Unable to generate QR code");

      const blob = await pdf(
        <QrCodePdf providers={providers} qrData={qrData} venue={venue} />
      ).toBlob();
      const objectUrl = URL.createObjectURL(blob);
      const downloadLink = document.createElement("a");

      downloadLink.href = objectUrl;
      downloadLink.download = `${venue.name} QR.pdf`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
    } catch (error) {
      errorModal(error);
    } finally {
      setDownloadingVenueId(undefined);
    }
  };

  return (
    <div style={styles.formWrapper}>
      <div style={styles.tableWrapper}>
        <table style={styles.tg}>
          <thead>
            <tr>
              <Th textAlign="left">VENUES</Th>
              <Th>PIN</Th>
              <Th>QR CODE</Th>
              <Th>DOWNLOAD</Th>
            </tr>
          </thead>
          <tbody>
            {updatedVenues?.map((item, idx) => {
              return (
                <tr key={`${item?.name} + ${idx}`}>
                  <TableDiv>{item?.name}</TableDiv>
                  <td style={styles.pinCheckBox}>
                    <RotaCheckbox
                      isChecked={item?.isPinCodeCheckInEnabled}
                      onClick={() => handlePinCodeChecked(item)}
                    />
                  </td>
                  <td style={styles.qrCheckBox}>
                    <RotaCheckbox
                      isChecked={item?.isQrCodeCheckInEnabled}
                      onClick={() => handleQrCodeChecked(item)}
                    />
                  </td>
                  <td style={styles.tableDiv}>
                    <RotaButtonNew
                      variant="outlined"
                      isDisabled={
                        !item?.isQrCodeCheckInEnabled || !!downloadingVenueId
                      }
                      isLoading={downloadingVenueId === item?.id}
                      iconLeft={<Download />}
                      onClick={() => qrCodeDownload(item)}
                    >
                      QR Code
                    </RotaButtonNew>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <RotaButtonNew
        variant="text"
        onClick={event => clearAllCheckBoxHandler(event)}
      >
        Clear All
      </RotaButtonNew>
      <SaveButtonWrapper>
        <RotaButtonNew
          onClick={saveVenue}
          isSuccess={shouldShowSavedState}
          isLoading={isStateLoading}
        >
          {shouldShowSavedState ? "Saved" : "Save"}
        </RotaButtonNew>
      </SaveButtonWrapper>
    </div>
  );
};

export default getProfile(createOrUpdateVenue(CheckInLocationForm));
